import LegalLayout from '../components/LegalLayout'
import SeoHead from '../components/SeoHead'
import { LEGAL, SITE } from '../lib/links'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

const UPDATED_AT = '3 août 2026'

export default function TermsPage() {
  const { t } = useT()
  const locale = useLocale()
  if (locale === 'en') return <TermsPageEn />
  if (locale === 'nl') return <TermsPageNl />
  return (
    <>
      <SeoHead
        title="Conditions générales de vente — Yaslan"
        description="Conditions générales de vente et de prestation de services logistiques de Yaslan Logistics BV. Stockage, pick & pack, expédition, retours."
        path="/cgv"
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.terms'), path: localizedPath('terms', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="CGV"
        badge="Légal · CGV"
        title={
          <>
            Conditions{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              générales de vente
            </span>
            .
          </>
        }
        intro={
          <>
            Les présentes conditions régissent toute prestation de services logistiques fournie par
            Yaslan Logistics BV, sous la marque Yaslan, à ses clients professionnels.
          </>
        }
        updatedAt={UPDATED_AT}
      >
        <h2>1. Objet et champ d'application</h2>
        <p>
          Les présentes Conditions Générales de Vente (ci-après «&nbsp;CGV&nbsp;») s'appliquent à
          toute prestation de services logistiques (stockage, préparation de commandes, emballage,
          expédition, gestion des retours et services connexes) fournie par <strong>{LEGAL.company}</strong>{' '}
          ({LEGAL.legalForm}), exploitant la marque «&nbsp;{LEGAL.brand}&nbsp;» (ci-après «&nbsp;le Prestataire&nbsp;»),
          au profit de tout client professionnel (ci-après «&nbsp;le Client&nbsp;»).
        </p>
        <p>
          Le Prestataire est inscrit à la Banque-Carrefour des Entreprises sous le numéro {LEGAL.bce}, TVA {LEGAL.vat}.
          Son siège social est établi à {LEGAL.registeredOffice.city} et son entrepôt d'exploitation
          à {LEGAL.warehouse.city} (Belgique).
        </p>
        <p>
          Toute commande de prestations implique l'acceptation pleine et entière des présentes CGV,
          qui prévalent sur toute condition d'achat du Client, sauf dérogation écrite expresse.
        </p>

        <h2>2. Définitions</h2>
        <ul>
          <li><strong>Marchandises</strong>&nbsp;: produits remis par le Client au Prestataire pour stockage, préparation et/ou expédition</li>
          <li><strong>Devis</strong>&nbsp;: document détaillant les prestations, tarifs et conditions, valable 30 jours</li>
          <li><strong>Contrat</strong>&nbsp;: l'ensemble formé par le devis signé, la grille tarifaire et les présentes CGV</li>
          <li><strong>Cut-off</strong>&nbsp;: heure limite quotidienne au-delà de laquelle une commande est traitée le jour ouvré suivant</li>
          <li><strong>Transporteur</strong>&nbsp;: tiers chargé d'acheminer les Marchandises au destinataire final</li>
        </ul>

        <h2>3. Formation du contrat</h2>
        <p>
          Le contrat est formé par la signature du devis ou par la confirmation écrite (e-mail
          inclus) du Client acceptant l'offre du Prestataire. La signature peut être manuscrite
          ou électronique.
        </p>

        <h2>4. Prestations</h2>
        <p>Le Prestataire propose, selon le devis&nbsp;:</p>
        <ul>
          <li><strong>Stockage</strong>&nbsp;: réception, contrôle qualité, mise en stock et inventaire des Marchandises</li>
          <li><strong>Pick &amp; Pack</strong>&nbsp;: préparation, emballage et étiquetage des commandes</li>
          <li><strong>Expédition</strong>&nbsp;: remise des colis aux Transporteurs partenaires</li>
          <li><strong>Retours</strong>&nbsp;: réception, contrôle, remise en stock ou destruction des Marchandises retournées</li>
          <li><strong>Services additionnels</strong>&nbsp;: kitting, personnalisation, étiquetage spécifique, gestion des certificats douaniers</li>
        </ul>

        <h2>5. Tarifs et facturation</h2>
        <p>
          Les tarifs figurent dans le devis ou la grille tarifaire annexée. Ils s'entendent hors
          TVA (21% en Belgique) et hors frais de transport.
        </p>
        <p>
          La facturation est mensuelle, à terme échu. Sont facturés notamment&nbsp;: stockage au
          m³ ou à l'emplacement, préparation à la commande/à la ligne, emballage, frais de
          transport refacturés, services additionnels.
        </p>
        <p>
          Les factures sont payables à <strong>30 jours date de facture</strong> par virement bancaire.
          Tout retard de paiement entraîne de plein droit, sans mise en demeure&nbsp;:
        </p>
        <ul>
          <li>Un intérêt de retard au taux légal applicable aux opérations B2B (loi du 2 août 2002)</li>
          <li>Une indemnité forfaitaire de 40&nbsp;€ pour frais de recouvrement (art. 6 de la loi précitée)</li>
          <li>Une indemnité conventionnelle de 10% des sommes dues, avec un minimum de 75&nbsp;€</li>
        </ul>
        <p>
          Le Prestataire se réserve le droit de suspendre toute prestation, y compris la livraison
          des Marchandises stockées, en cas d'impayé non régularisé après mise en demeure restée
          infructueuse pendant 8&nbsp;jours.
        </p>

        <h2>6. Révision tarifaire</h2>
        <p>
          Les tarifs sont révisables annuellement, notamment en fonction de l'évolution de l'indice
          des prix à la consommation, des coûts de transport négociés et des charges salariales.
          Le Client est informé par écrit au moins 30 jours avant l'entrée en vigueur d'une
          révision.
        </p>

        <h2>7. Obligations du Client</h2>
        <p>Le Client s'engage à&nbsp;:</p>
        <ul>
          <li>Fournir des Marchandises conformes à la législation et correctement référencées (SKU, EAN, fiche produit)</li>
          <li>Notifier au Prestataire toute Marchandise dangereuse, fragile, soumise à régime particulier (denrées périssables, produits ADR, alcool, etc.)</li>
          <li>Garantir l'origine licite des Marchandises et l'absence de contrefaçon</li>
          <li>Souscrire une assurance couvrant les Marchandises stockées (voir art. 11)</li>
          <li>Respecter les délais et procédures de cut-off</li>
          <li>Régler les factures dans les délais convenus</li>
        </ul>

        <h2>8. Obligations du Prestataire</h2>
        <p>Le Prestataire s'engage à&nbsp;:</p>
        <ul>
          <li>Stocker les Marchandises dans un entrepôt sécurisé, conforme aux normes en vigueur</li>
          <li>Préparer et expédier les commandes dans les délais convenus, sous réserve du respect du cut-off par le Client</li>
          <li>Mettre à disposition un suivi en temps réel via le dashboard</li>
          <li>Fournir un support client en français et en anglais</li>
        </ul>
        <p>
          Le Prestataire est tenu d'une <strong>obligation de moyens</strong>, et non de résultat, dans la
          mesure où l'acheminement final dépend de tiers transporteurs.
        </p>

        <h2>9. Cut-off et délais d'expédition</h2>
        <p>
          Sauf mention contraire au devis, le cut-off quotidien est fixé à <strong>15h00</strong> les
          jours ouvrés. Toute commande validée et payée avant cette heure est expédiée le jour
          même. Les commandes reçues après le cut-off sont traitées le jour ouvré suivant.
        </p>
        <p>
          Les délais de livraison annoncés sont indicatifs et dépendent du Transporteur sélectionné.
          Aucune indemnité ne pourra être réclamée au Prestataire en cas de retard imputable au Transporteur.
        </p>

        <h2>10. Réception et conformité des Marchandises</h2>
        <p>
          À la réception, le Prestataire effectue un contrôle quantitatif et un contrôle visuel
          de l'état extérieur des emballages. Toute anomalie (manquant, casse apparente, palette
          endommagée) est notifiée au Client dans un délai de 5 jours ouvrés, à défaut de quoi
          les Marchandises sont réputées conformes en quantité.
        </p>
        <p>
          Le Prestataire ne procède pas à un contrôle qualitatif détaillé du contenu des cartons
          fermés, sauf prestation spécifique commandée et facturée.
        </p>

        <h2>11. Assurance et responsabilité</h2>
        <p>
          Le Prestataire est assuré en responsabilité civile professionnelle et en responsabilité
          de l'entreposeur. <strong>L'assurance des Marchandises stockées contre les risques de perte,
          vol et incendie incombe au Client</strong>, qui s'engage à souscrire une police adéquate.
        </p>
        <p>
          La responsabilité du Prestataire, quelle qu'en soit la cause, est limitée à&nbsp;:
        </p>
        <ul>
          <li>Pour les pertes ou avaries durant le stockage&nbsp;: la valeur d'achat hors taxes des Marchandises concernées, plafonnée à 25&nbsp;€/kg et au total des prestations facturées sur les 3 derniers mois</li>
          <li>Pour les opérations de pick &amp; pack&nbsp;: le coût de la prestation concernée</li>
          <li>Pour les retards d'expédition imputables au Prestataire&nbsp;: le coût des frais de port concernés</li>
        </ul>
        <p>
          Le Prestataire ne saurait être tenu responsable des dommages indirects (perte de chiffre
          d'affaires, perte de clientèle, atteinte à l'image, etc.).
        </p>

        <h2>12. Force majeure</h2>
        <p>
          Aucune partie ne saurait être tenue responsable d'un manquement résultant d'un cas de
          force majeure, en ce compris notamment&nbsp;: incendie, inondation, catastrophe naturelle,
          grève (y compris des Transporteurs), pandémie, guerre, attentat, décision administrative,
          panne d'infrastructure publique, cyber-attaque massive.
        </p>

        <h2>13. Durée et résiliation</h2>
        <p>
          Sauf mention contraire au devis, le contrat est conclu pour une durée indéterminée. Chaque
          partie peut y mettre fin moyennant un préavis de <strong>3 mois</strong>, notifié par e-mail avec
          accusé de réception ou par courrier recommandé.
        </p>
        <p>
          En cas de manquement grave d'une partie à ses obligations, l'autre partie peut résilier
          le contrat de plein droit, sans indemnité, après mise en demeure restée infructueuse
          pendant 30 jours.
        </p>
        <p>
          À la cessation du contrat, le Client dispose de <strong>30 jours</strong> pour récupérer les
          Marchandises stockées, à ses frais. Au-delà, des frais de stockage majorés de 50% seront
          facturés et le Prestataire pourra, après nouvelle mise en demeure, faire procéder à la
          vente ou à la destruction des Marchandises aux frais du Client.
        </p>

        <h2>14. Confidentialité</h2>
        <p>
          Chaque partie s'engage à préserver la confidentialité des informations commerciales,
          techniques et financières échangées dans le cadre du contrat, pendant toute sa durée et
          5 ans après son terme.
        </p>

        <h2>15. Données personnelles</h2>
        <p>
          Dans le cadre des prestations, le Prestataire traite des données personnelles pour le
          compte du Client (notamment les coordonnées des destinataires de colis) en qualité de
          sous-traitant au sens du RGPD. Les modalités de ce traitement sont précisées dans un
          accord de traitement de données (DPA) annexé au devis. Pour le reste, voir notre Politique
          de confidentialité.
        </p>

        <h2>16. Droit applicable et juridiction compétente</h2>
        <p>
          Les présentes CGV sont régies par le <strong>droit belge</strong>. Tout litige relatif à leur
          formation, leur interprétation ou leur exécution relève de la compétence exclusive du{' '}
          <strong>{LEGAL.jurisdiction.fr}</strong>, nonobstant pluralité de défendeurs ou appel en garantie.
        </p>

        <h2>17. Contact</h2>
        <p>
          Pour toute question relative aux présentes CGV, merci d'écrire à&nbsp;
          <a href={SITE.emailHref}>{SITE.email}</a>.
        </p>
      </LegalLayout>
    </>
  )
}

function TermsPageEn() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Terms & Conditions — Yaslan"
        description="General terms and conditions for logistics services provided by Yaslan Logistics BV. Storage, pick & pack, shipping, returns."
        path={localizedPath('terms', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.terms'), path: localizedPath('terms', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Terms & Conditions"
        badge="Legal · T&amp;Cs"
        title={
          <>
            Terms &amp;{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              Conditions
            </span>
            .
          </>
        }
        intro={
          <>
            These terms govern all logistics services provided by Yaslan Logistics BV, trading under
            the Yaslan brand, to its business clients.
          </>
        }
        updatedAt="3 August 2026"
      >
        <h2>1. Subject matter and scope</h2>
        <p>
          These General Terms and Conditions (hereinafter "T&amp;Cs") apply to all logistics
          services (storage, order fulfilment, packaging, shipping, returns management and related
          services) provided by <strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), trading under
          the «&nbsp;{LEGAL.brand}&nbsp;» brand (hereinafter "the Service Provider"), to any
          business client (hereinafter "the Client").
        </p>
        <p>
          The Service Provider is registered with the Crossroads Bank for Enterprises under
          company number {LEGAL.bce}, VAT {LEGAL.vat}. Its registered office is in{' '}
          {LEGAL.registeredOffice.city} and its operating warehouse in {LEGAL.warehouse.city}, Belgium.
        </p>
        <p>
          Any order for services implies full and unconditional acceptance of these T&amp;Cs, which
          prevail over any of the Client's purchasing conditions, unless expressly agreed otherwise
          in writing.
        </p>

        <h2>2. Definitions</h2>
        <ul>
          <li><strong>Goods</strong>&nbsp;: products entrusted by the Client to the Service Provider for storage, fulfilment and/or shipping</li>
          <li><strong>Quote</strong>&nbsp;: document detailing the services, rates and conditions, valid for 30 days</li>
          <li><strong>Contract</strong>&nbsp;: the whole formed by the signed quote, the rate schedule and these T&amp;Cs</li>
          <li><strong>Cut-off</strong>&nbsp;: daily deadline beyond which an order is processed on the next business day</li>
          <li><strong>Carrier</strong>&nbsp;: third party responsible for delivering the Goods to the final recipient</li>
        </ul>

        <h2>3. Formation of contract</h2>
        <p>
          The contract is formed upon signature of the quote or upon written confirmation (including
          by e-mail) from the Client accepting the Service Provider's offer. The signature may be
          handwritten or electronic.
        </p>

        <h2>4. Services</h2>
        <p>The Service Provider offers, as set out in the quote:</p>
        <ul>
          <li><strong>Storage</strong>&nbsp;: receipt, quality check, warehousing and stock-taking of Goods</li>
          <li><strong>Pick &amp; Pack</strong>&nbsp;: order preparation, packaging and labelling</li>
          <li><strong>Shipping</strong>&nbsp;: handover of parcels to partner Carriers</li>
          <li><strong>Returns</strong>&nbsp;: receipt, inspection, restocking or destruction of returned Goods</li>
          <li><strong>Additional services</strong>&nbsp;: kitting, customisation, specific labelling, customs documentation management</li>
        </ul>

        <h2>5. Pricing and invoicing</h2>
        <p>
          Rates are set out in the quote or the appended rate schedule. All prices are exclusive
          of VAT (21% in Belgium) and exclusive of transport costs.
        </p>
        <p>
          Invoicing is monthly, in arrears. Items billed include, in particular: storage per m³
          or per location, fulfilment per order/per line, packaging, pass-through transport costs,
          additional services.
        </p>
        <p>
          Invoices are payable within <strong>30 days of the invoice date</strong> by bank transfer.
          Any late payment automatically entails, without prior notice:
        </p>
        <ul>
          <li>Late-payment interest at the statutory rate applicable to B2B transactions (Act of 2 August 2002)</li>
          <li>A flat-rate recovery fee of 40&nbsp;€ (Art. 6 of the aforementioned Act)</li>
          <li>A contractual penalty of 10% of the amounts due, with a minimum of 75&nbsp;€</li>
        </ul>
        <p>
          The Service Provider reserves the right to suspend all services, including delivery of
          stored Goods, in the event of an unpaid invoice not settled within 8&nbsp;days of a
          formal notice remaining unanswered.
        </p>

        <h2>6. Price revision</h2>
        <p>
          Rates may be revised annually, in particular in line with changes in the consumer price
          index, negotiated transport costs and payroll costs. The Client will be notified in
          writing at least 30 days before any revision takes effect.
        </p>

        <h2>7. Client obligations</h2>
        <p>The Client undertakes to:</p>
        <ul>
          <li>Supply Goods that comply with applicable law and are properly referenced (SKU, EAN, product data sheet)</li>
          <li>Notify the Service Provider of any hazardous, fragile or specially regulated Goods (perishables, ADR goods, alcohol, etc.)</li>
          <li>Warrant the lawful origin of the Goods and the absence of counterfeit items</li>
          <li>Take out insurance covering the stored Goods (see Art. 11)</li>
          <li>Comply with cut-off deadlines and procedures</li>
          <li>Pay invoices within the agreed timeframes</li>
        </ul>

        <h2>8. Service Provider obligations</h2>
        <p>The Service Provider undertakes to:</p>
        <ul>
          <li>Store Goods in a secure warehouse complying with applicable standards</li>
          <li>Prepare and dispatch orders within the agreed timeframes, subject to the Client respecting the cut-off</li>
          <li>Provide real-time tracking via the dashboard</li>
          <li>Provide client support in French and English</li>
        </ul>
        <p>
          The Service Provider is bound by a <strong>best-efforts obligation</strong>, and not an
          obligation of result, insofar as final delivery depends on third-party Carriers.
        </p>

        <h2>9. Cut-off and dispatch times</h2>
        <p>
          Unless otherwise stated in the quote, the daily cut-off is set at <strong>15:00</strong> on
          business days. Any order validated and paid before this time is dispatched the same day.
          Orders received after the cut-off are processed on the next business day.
        </p>
        <p>
          Announced delivery times are indicative and depend on the selected Carrier. No
          compensation may be claimed from the Service Provider for delays attributable to the
          Carrier.
        </p>

        <h2>10. Receipt and conformity of Goods</h2>
        <p>
          Upon receipt, the Service Provider performs a quantitative check and a visual inspection
          of the outer condition of the packaging. Any anomaly (shortage, apparent breakage,
          damaged pallet) is notified to the Client within 5 business days, failing which the
          Goods are deemed conforming in quantity.
        </p>
        <p>
          The Service Provider does not carry out a detailed qualitative inspection of the contents
          of sealed cartons, except where a specific service has been ordered and invoiced.
        </p>

        <h2>11. Insurance and liability</h2>
        <p>
          The Service Provider holds professional civil liability insurance and warehouse keeper's
          liability insurance. <strong>Insurance of stored Goods against the risks of loss, theft
          and fire is the Client's responsibility</strong>; the Client undertakes to take out an
          adequate policy.
        </p>
        <p>
          The Service Provider's liability, whatever its cause, is limited to:
        </p>
        <ul>
          <li>For loss or damage during storage: the ex-tax purchase value of the Goods concerned, capped at 25&nbsp;€/kg and at the total of services invoiced over the preceding 3 months</li>
          <li>For pick &amp; pack operations: the cost of the service concerned</li>
          <li>For dispatch delays attributable to the Service Provider: the cost of the relevant shipping charges</li>
        </ul>
        <p>
          The Service Provider shall not be liable for indirect damages (loss of revenue, loss of
          customers, reputational damage, etc.).
        </p>

        <h2>12. Force majeure</h2>
        <p>
          Neither party shall be held liable for a breach resulting from a force majeure event,
          including in particular: fire, flood, natural disaster, strike (including by Carriers),
          pandemic, war, terrorist attack, administrative decision, public infrastructure failure,
          large-scale cyber-attack.
        </p>

        <h2>13. Term and termination</h2>
        <p>
          Unless otherwise stated in the quote, the contract is concluded for an indefinite term.
          Either party may terminate it by giving <strong>3 months'</strong> notice, sent by e-mail
          with acknowledgement of receipt or by registered post.
        </p>
        <p>
          In the event of a material breach by one party of its obligations, the other party may
          terminate the contract by operation of law, without compensation, after a formal notice
          has remained unanswered for 30 days.
        </p>
        <p>
          Upon termination of the contract, the Client has <strong>30 days</strong> to collect the
          stored Goods, at its own expense. Beyond this period, storage charges increased by 50%
          will be invoiced and the Service Provider may, after a further formal notice, arrange for
          the sale or destruction of the Goods at the Client's expense.
        </p>

        <h2>14. Confidentiality</h2>
        <p>
          Each party undertakes to maintain the confidentiality of the commercial, technical and
          financial information exchanged in the course of the contract, throughout its duration
          and for 5 years after its expiry.
        </p>

        <h2>15. Personal data</h2>
        <p>
          In the course of providing the services, the Service Provider processes personal data on
          behalf of the Client (in particular the contact details of parcel recipients) acting as a
          processor within the meaning of the GDPR. The terms of this processing are set out in a
          data processing agreement (DPA) appended to the quote. For all other matters, see our
          Privacy Policy.
        </p>

        <h2>16. Governing law and jurisdiction</h2>
        <p>
          These T&amp;Cs are governed by <strong>Belgian law</strong>. Any dispute relating to their
          formation, interpretation or performance falls under the exclusive jurisdiction of the{' '}
          <strong>{LEGAL.jurisdiction.en}</strong>, notwithstanding multiple defendants or warranty
          claims.
        </p>

        <h2>17. Contact</h2>
        <p>
          For any question relating to these T&amp;Cs, please write to&nbsp;
          <a href={SITE.emailHref}>{SITE.email}</a>.
        </p>
      </LegalLayout>
    </>
  )
}

function TermsPageNl() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Algemene voorwaarden — Yaslan"
        description="Algemene voorwaarden voor logistieke diensten van Yaslan Logistics BV. Opslag, pick & pack, verzending, retouren."
        path={localizedPath('terms', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.terms'), path: localizedPath('terms', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Algemene voorwaarden"
        badge="Juridisch · AV"
        title={
          <>
            Algemene{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              voorwaarden
            </span>
            .
          </>
        }
        intro={
          <>
            Deze voorwaarden zijn van toepassing op alle logistieke diensten die door Yaslan Logistics
            BV, onder het merk Yaslan, worden verleend aan professionele klanten.
          </>
        }
        updatedAt="3 augustus 2026"
      >
        <h2>1. Voorwerp en toepassingsgebied</h2>
        <p>
          Deze Algemene Voorwaarden (hierna "AV") zijn van toepassing op alle logistieke diensten
          (opslag, ordervoorbereiding, verpakking, verzending, retourverwerking en aanverwante
          diensten) verleend door <strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), handelend
          onder het merk «&nbsp;{LEGAL.brand}&nbsp;» (hierna "de Dienstverlener"), aan elke
          professionele klant (hierna "de Klant").
        </p>
        <p>
          De Dienstverlener is ingeschreven bij de Kruispuntbank van Ondernemingen onder
          ondernemingsnummer {LEGAL.bce}, btw {LEGAL.vat}. De maatschappelijke zetel is gevestigd
          te {LEGAL.registeredOffice.city} en het exploitatiemagazijn te {LEGAL.warehouse.city} (België).
        </p>
        <p>
          Elke opdracht tot dienstverlening impliceert de volledige en onvoorwaardelijke aanvaarding
          van deze AV, die voorrang hebben op alle aankoopvoorwaarden van de Klant, tenzij
          schriftelijk uitdrukkelijk anders is overeengekomen.
        </p>

        <h2>2. Definities</h2>
        <ul>
          <li><strong>Goederen</strong>&nbsp;: producten die de Klant aan de Dienstverlener toevertrouwt voor opslag, voorbereiding en/of verzending</li>
          <li><strong>Offerte</strong>&nbsp;: document met de beschrijving van de diensten, tarieven en voorwaarden, geldig gedurende 30 dagen</li>
          <li><strong>Overeenkomst</strong>&nbsp;: het geheel gevormd door de ondertekende offerte, het tariefrooster en deze AV</li>
          <li><strong>Cut-off</strong>&nbsp;: dagelijkse deadline waarna een bestelling wordt verwerkt op de eerstvolgende werkdag</li>
          <li><strong>Vervoerder</strong>&nbsp;: derde die instaat voor de bezorging van de Goederen aan de eindgeadresseerde</li>
        </ul>

        <h2>3. Totstandkoming van de overeenkomst</h2>
        <p>
          De overeenkomst komt tot stand door ondertekening van de offerte of door schriftelijke
          bevestiging (e-mail inbegrepen) van de Klant die het aanbod van de Dienstverlener
          aanvaardt. De ondertekening kan handmatig of elektronisch zijn.
        </p>

        <h2>4. Diensten</h2>
        <p>De Dienstverlener biedt, overeenkomstig de offerte:</p>
        <ul>
          <li><strong>Opslag</strong>&nbsp;: ontvangst, kwaliteitscontrole, inslag en inventarisatie van Goederen</li>
          <li><strong>Pick &amp; Pack</strong>&nbsp;: ordervoorbereiding, verpakking en etikettering</li>
          <li><strong>Verzending</strong>&nbsp;: overdracht van pakketten aan partnervervoerders</li>
          <li><strong>Retouren</strong>&nbsp;: ontvangst, controle, terug inslag of vernietiging van geretourneerde Goederen</li>
          <li><strong>Aanvullende diensten</strong>&nbsp;: kitting, personalisatie, specifieke etikettering, beheer van douanedocumenten</li>
        </ul>

        <h2>5. Tarieven en facturatie</h2>
        <p>
          De tarieven zijn vermeld in de offerte of het bijgevoegde tariefrooster. Ze zijn exclusief
          btw (21% in België) en exclusief transportkosten.
        </p>
        <p>
          Facturatie geschiedt maandelijks, achteraf. Gefactureerde posten omvatten onder meer:
          opslag per m³ of per locatie, orderpicking per bestelling/per regel, verpakking,
          doorgefactureerde transportkosten, aanvullende diensten.
        </p>
        <p>
          Facturen zijn betaalbaar binnen <strong>30 dagen na factuurdatum</strong> per bankoverschrijving.
          Elke betalingsachterstand brengt van rechtswege en zonder ingebrekestelling met zich mee:
        </p>
        <ul>
          <li>Verwijlrente tegen de wettelijke rentevoet van toepassing op B2B-transacties (wet van 2 augustus 2002)</li>
          <li>Een forfaitaire invorderingsvergoeding van 40&nbsp;€ (art. 6 van voornoemde wet)</li>
          <li>Een contractuele schadevergoeding van 10% van de verschuldigde bedragen, met een minimum van 75&nbsp;€</li>
        </ul>
        <p>
          De Dienstverlener behoudt zich het recht voor alle diensten op te schorten, inclusief de
          afgifte van opgeslagen Goederen, bij een onbetaalde factuur die niet wordt geregeld binnen
          8&nbsp;dagen na een vruchteloos gebleven ingebrekestelling.
        </p>

        <h2>6. Tariefherziening</h2>
        <p>
          Tarieven kunnen jaarlijks worden herzien, met name op basis van de evolutie van de
          consumentenprijsindex, de onderhandelde transportkosten en de loonkosten. De Klant wordt
          schriftelijk ingelicht ten minste 30 dagen vóór de inwerkingtreding van een herziening.
        </p>

        <h2>7. Verplichtingen van de Klant</h2>
        <p>De Klant verbindt zich ertoe:</p>
        <ul>
          <li>Goederen te leveren die voldoen aan de wetgeving en correct zijn gerefereerd (SKU, EAN, productfiche)</li>
          <li>De Dienstverlener te informeren over gevaarlijke, breekbare of bijzonder gereguleerde Goederen (bederfbare producten, ADR-goederen, alcohol, enz.)</li>
          <li>De rechtmatige herkomst van de Goederen en de afwezigheid van namaak te garanderen</li>
          <li>Een verzekering af te sluiten die de opgeslagen Goederen dekt (zie art. 11)</li>
          <li>De cut-offtijden en -procedures na te leven</li>
          <li>Facturen binnen de overeengekomen termijnen te betalen</li>
        </ul>

        <h2>8. Verplichtingen van de Dienstverlener</h2>
        <p>De Dienstverlener verbindt zich ertoe:</p>
        <ul>
          <li>Goederen op te slaan in een beveiligd magazijn dat voldoet aan de geldende normen</li>
          <li>Bestellingen voor te bereiden en te verzenden binnen de overeengekomen termijnen, mits de Klant de cut-off naleeft</li>
          <li>Real-time opvolging te bieden via het dashboard</li>
          <li>Klantenondersteuning te bieden in het Frans en het Engels</li>
        </ul>
        <p>
          De Dienstverlener is gehouden aan een <strong>middelenverbintenis</strong>, en niet aan een
          resultaatsverbintenis, voor zover de uiteindelijke aflevering afhankelijk is van externe
          vervoerders.
        </p>

        <h2>9. Cut-off en verzendtermijnen</h2>
        <p>
          Tenzij anders vermeld in de offerte, is de dagelijkse cut-off vastgesteld op <strong>15u00</strong>{' '}
          op werkdagen. Elke bestelling die vóór dat tijdstip is gevalideerd en betaald, wordt
          dezelfde dag verzonden. Bestellingen die na de cut-off worden ontvangen, worden verwerkt
          op de eerstvolgende werkdag.
        </p>
        <p>
          De meegedeelde levertijden zijn indicatief en afhankelijk van de gekozen Vervoerder. Er
          kan geen schadevergoeding worden gevorderd van de Dienstverlener voor vertragingen die aan
          de Vervoerder zijn toe te schrijven.
        </p>

        <h2>10. Ontvangst en conformiteit van de Goederen</h2>
        <p>
          Bij ontvangst voert de Dienstverlener een kwantitatieve controle en een visuele inspectie
          van de buitenkant van de verpakkingen uit. Elke afwijking (tekort, zichtbare breuk,
          beschadigde pallet) wordt binnen 5 werkdagen aan de Klant gemeld; bij gebrek hieraan
          worden de Goederen geacht conform te zijn wat de hoeveelheid betreft.
        </p>
        <p>
          De Dienstverlener voert geen gedetailleerde kwalitatieve inspectie uit van de inhoud van
          gesloten dozen, tenzij een specifieke dienst is besteld en gefactureerd.
        </p>

        <h2>11. Verzekering en aansprakelijkheid</h2>
        <p>
          De Dienstverlener beschikt over een beroepsaansprakelijkheidsverzekering en een
          magazijnhoudersverzekering. <strong>De verzekering van opgeslagen Goederen tegen verlies,
          diefstal en brand is de verantwoordelijkheid van de Klant</strong>, die zich ertoe verbindt
          een adequate polis af te sluiten.
        </p>
        <p>
          De aansprakelijkheid van de Dienstverlener, ongeacht de oorzaak, is beperkt tot:
        </p>
        <ul>
          <li>Voor verlies of beschadiging tijdens de opslag: de inkoopwaarde exclusief belastingen van de betrokken Goederen, met een maximum van 25&nbsp;€/kg en het totaal van de gefactureerde diensten over de voorgaande 3 maanden</li>
          <li>Voor pick &amp; pack-operaties: de kosten van de betrokken dienst</li>
          <li>Voor verzendvertragingen toe te schrijven aan de Dienstverlener: de kosten van de betrokken verzending</li>
        </ul>
        <p>
          De Dienstverlener is niet aansprakelijk voor indirecte schade (omzetverlies, verlies van
          klanten, reputatieschade, enz.).
        </p>

        <h2>12. Overmacht</h2>
        <p>
          Geen van de partijen kan aansprakelijk worden gesteld voor een tekortkoming als gevolg
          van overmacht, waaronder met name: brand, overstroming, natuurramp, staking (ook van
          Vervoerders), pandemie, oorlog, terroristische aanslag, administratieve beslissing,
          storing van openbare infrastructuur, grootschalige cyberaanval.
        </p>

        <h2>13. Duur en beëindiging</h2>
        <p>
          Tenzij anders vermeld in de offerte, wordt de overeenkomst voor onbepaalde duur gesloten.
          Elke partij kan de overeenkomst beëindigen mits een opzegtermijn van <strong>3 maanden</strong>,
          meegedeeld per e-mail met ontvangstbevestiging of per aangetekende brief.
        </p>
        <p>
          Bij een ernstige tekortkoming van een partij in haar verplichtingen kan de andere partij
          de overeenkomst van rechtswege en zonder vergoeding beëindigen, nadat een ingebrekestelling
          gedurende 30 dagen vruchteloos is gebleven.
        </p>
        <p>
          Na het einde van de overeenkomst heeft de Klant <strong>30 dagen</strong> om de opgeslagen
          Goederen op te halen, op eigen kosten. Daarna worden opslagkosten verhoogd met 50%
          gefactureerd en kan de Dienstverlener, na een nieuwe ingebrekestelling, de Goederen op
          kosten van de Klant laten verkopen of vernietigen.
        </p>

        <h2>14. Vertrouwelijkheid</h2>
        <p>
          Elke partij verbindt zich ertoe de vertrouwelijkheid te bewaren van de commerciële,
          technische en financiële informatie die in het kader van de overeenkomst wordt uitgewisseld,
          gedurende de volledige looptijd en gedurende 5 jaar na de beëindiging ervan.
        </p>

        <h2>15. Persoonsgegevens</h2>
        <p>
          In het kader van de dienstverlening verwerkt de Dienstverlener persoonsgegevens namens de
          Klant (met name de contactgegevens van pakketontvangers) als verwerker in de zin van de
          AVG (GDPR). De modaliteiten van deze verwerking worden uiteengezet in een
          gegevensverwerkingsovereenkomst (DPA) die bij de offerte is gevoegd. Zie voor het overige
          ons Privacybeleid.
        </p>

        <h2>16. Toepasselijk recht en bevoegde rechter</h2>
        <p>
          Deze AV worden beheerst door het <strong>Belgisch recht</strong>. Elk geschil met betrekking
          tot hun totstandkoming, interpretatie of uitvoering valt onder de exclusieve bevoegdheid
          van de <strong>{LEGAL.jurisdiction.nl}</strong>, onverminderd meervoudige verweerders of
          vrijwaringsvordering.
        </p>

        <h2>17. Contact</h2>
        <p>
          Voor vragen over deze AV kunt u schrijven naar&nbsp;
          <a href={SITE.emailHref}>{SITE.email}</a>.
        </p>
      </LegalLayout>
    </>
  )
}
